import {useState, useEffect, useCallback, useRef} from '@wordpress/element';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {useDispatch, useSelect} from '@wordpress/data';
import classnames from 'classnames';
import {__} from '@wordpress/i18n';
import {STORE_KEY} from '@wc-braintree/block-data';
import {getSettings, loadClient, getClientToken, canShowSavedCard} from "../utils";
import {
    PaymentMethod,
    PaymentMethodLabel,
    SavedTokenProvider,
    Notice,
    FastlaneLink,
    FastlaneModal
} from '../../components';
import DropinForm from "./dropin-form";
import CustomForm from "./custom-form";
import {usePaymentMethodDataContext} from "../context";
import TokenizedCard from './tokenized-card'
import DropinPreview from './dropin-preview';

const getData = getSettings('braintree_cc');
const data = getSetting('braintree_cc_data');

const CreditCardComponent = (props) => {
    const [openModal, setOpenModal] = useState(false);
    const {
        billing,
        shippingData
    } = props;
    const {
        email
    } = billing.billingAddress;
    const {
        notice,
        fastlane: {
            fastlane,
            paymentToken,
            customerFound
        }
    } = usePaymentMethodDataContext();

    const {
        setPaymentToken
    } = useDispatch(STORE_KEY);

    const {
        emails
    } = useSelect(select => {
        return {
            emails: select(STORE_KEY).getCanceledEmails()
        }
    });

    const showSignup = data.showSignup && !emails.includes(email);

    const dropinEnabled = getData('dropinEnabled');
    const Content = dropinEnabled ? DropinContainer : HostedFieldsContainer;
    const classes = classnames('wc-braintree-card-container', {
        'dropin': dropinEnabled,
        'customForm': !dropinEnabled
    });

    const onTokenize = useCallback((token) => {
        setPaymentToken(token);
    }, []);

    if (paymentToken) {
        return (
            <TokenizedCard
                data={data}
                token={paymentToken}
                fastlane={fastlane}
                customerFound={customerFound}
                {...props}/>
        )
    }

    return (
        <>
            {showSignup &&
                <FastlaneLink
                    logo={data.fastLaneLogo}
                    text={data.i18n.fastlane_signup}
                    onClick={() => setOpenModal(true)}/>
            }
            {notice?.notice && <Notice notice={notice.notice} onRemove={notice.removeNotice}/>}
            <div className={classes}>
                <Content/>
            </div>
            <FastlaneModal
                i18n={data.i18n}
                open={openModal}
                onClose={() => setOpenModal(false)}
                fastlane={fastlane}
                onTokenize={onTokenize}
                shippingData={shippingData}
            />
        </>
    )
}

const HostedFieldsContainer = (props) => {
    const options = {
        fields: getData('hostedFieldsOptions'),
        styles: getData('hostedFieldsStyles'),
        icons: getData('icons')
    };
    return <CustomForm options={options} client={loadClient} id={getData('customForm')} {...props}/>
}

const DropinContainer = () => {
    const {onPaymentDataFilter} = usePaymentMethodDataContext();

    const currentData = useRef({dropinInstance: null});

    const setDropinInstance = useCallback(dropinInstance => {
        currentData.current = {
            ...currentData.current,
            dropinInstance
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onPaymentDataFilter((response, {nonceKey}) => {
            return new Promise((resolve, reject) => {
                const {dropinInstance} = currentData.current;
                dropinInstance.requestPaymentMethod((err, payload) => {
                    if (err) {
                        reject(err);
                    } else {
                        response.meta.paymentMethodData[nonceKey] = payload.nonce;
                        resolve({
                            ...response,
                            ...payload
                        });
                    }
                });
            })
        }, 1);

        return unsubscribe;
    }, [onPaymentDataFilter]);

    const options = {
        clientToken: getClientToken(),
        locale: 'en_US'
    }
    return (
        <DropinForm options={options} setDropinInstance={setDropinInstance}/>
    )
}

registerPaymentMethod({
    name: getData('name'),
    gatewayId: getData('name'),
    label: <PaymentMethodLabel
        title={getData('title')}
        paymentMethod={getData('name')}
        icon={getData('icon')}/>,
    ariaLabel: __('Credit Cards', 'woo-payment-gateway'),
    canMakePayment: () => true,
    content: <PaymentMethod
        content={CreditCardComponent}
        title={getData('title')}
        description={getData('description')}
        name={getData('name')}
        gatewayId={getData('name')}
        icon={getData('icon')}
        getData={getData}/>,
    savedTokenComponent: <SavedTokenProvider getData={getData}
                                             name={getData('name')}
                                             gatewayId={getData('name')}
                                             vaultedThreeDSecure={getData('vaultedThreeDSecure')}/>,
    edit: <DropinPreview src={getData('editor').icon}/>,
    supports: {
        showSavedCards: getData('features').includes('tokenization'),
        showSaveOption: canShowSavedCard(),
        features: getData('features')
    }
});